import React, {useEffect, useState, useRef } from 'react';
import { fetchFollowupsForAQuery } from '../../api/Followup';
import { Followup } from '../../types/FollowUp';
import { Query } from '../../types/Query';
import { fetchQueryById } from '../../api/Query';
import { fetchCustomerById } from '../../api/Customer';
import CustomerInfoModal from '../Customer/CustomerInfoModal';
import { formatDate } from '../../utils/date';
import '../../css/modal.css';

interface ViewFollowUpsModalProps {
  queryId: number;
  onClose: () => void;
  onLog?: (followupId: number) => void;
}

const ViewFollowUpsModal: React.FC<ViewFollowUpsModalProps> = ({
  queryId,
  onClose,
  onLog,
}) => {
  const hasFetched = useRef(false);
  const [followUps, setFollowUps] = useState<Followup[]>([]);
  const [query, setQuery] = useState<Query | null>(null);
  const [customerDetails, setCustomerDetails] = useState<any | null>(null);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    (async () => {
      try {
        const [fData, qData] = await Promise.all([
          fetchFollowupsForAQuery(queryId),
          fetchQueryById(queryId)
        ]);
        const sortedData = (fData || []).slice().sort((a: Followup, b: Followup) => {
          const parse = (x: Followup) => {
            const datePart = String(x.date).includes('T') ? String(x.date).split('T')[0] : String(x.date).substring(0,10);
            const timePart = (x.time || '').substring(0,5) || '00:00';
            return new Date(`${datePart}T${timePart}`).getTime();
          };
          return parse(b) - parse(a);
        });
        setFollowUps(sortedData);
        setQuery(qData || null);
      } catch (e) {
        setFollowUps([]);
        setQuery(null);
      }
    })();
  }, [queryId]);

  const getTargetFollowupId = (): number | null => {
    if (!followUps || followUps.length === 0) return null;
    const pending = followUps.find(f => !f.outcome || String(f.outcome).trim() === '' || String(f.outcome).toLowerCase() === 'pending');
    return (pending?.id as number) || (followUps[0].id as number) || null;
  };

  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h2 style={{ marginRight: 'auto' }}>Follow-up Timeline</h2>
          {onLog && (
            <button
              className="secondary"
              onClick={() => { const id = getTargetFollowupId(); if (id) onLog(id); }}
              title="Log a new interaction"
            >
              Log Interaction
            </button>
          )}
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {query && (
          <div className="quotation-card" style={{ marginBottom: 12 }}>
            <div className="quotation-header">Query Details</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <strong>Customer:</strong>
                {query.customer?.name ? (
                  <button
                    type="button"
                    style={{ marginLeft: 8, color: '#2563eb', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    onClick={async () => {
                      if (!query?.customer?.id) return;
                      try {
                        const c = await fetchCustomerById(query.customer.id);
                        setCustomerDetails(c);
                      } catch (e) {
                        setCustomerDetails(null);
                      }
                    }}
                  >
                    {query.customer.name}
                  </button>
                ) : (
                  <span style={{ marginLeft: 8 }}>N/A</span>
                )}
              </div>
              <div><strong>Type:</strong> {query.type}</div>
              <div><strong>Location:</strong> {query.location}</div>
              <div><strong>Date:</strong> {formatDate(query.queryDate)}</div>
              <div><strong>PAX:</strong> {query.pax}</div>
              <div><strong>Assignee:</strong> {query.assignedTo?.name || 'UnAssigned'}</div>
            </div>
          </div>
        )}
        {followUps.length === 0 ? (
          <p>No follow-ups available.</p>
        ) : (
          <div className="timeline-container">
            {followUps.map((f) => (
              <div key={f.id} className="timeline-item">
                <div className="timeline-dot" />
                <div className="timeline-content">
                  <div className="timeline-header">
                    <strong>{formatDate(f.date)} — {String(f.time || '').substring(0,5)}</strong>
                  </div>
                  <p><strong>Why:</strong> {f.description}</p>
                  <p><strong>Notes:</strong> {f.notes}</p>
                  <p><strong>Outcome:</strong> {f.outcome}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        {customerDetails && (
          <CustomerInfoModal customer={customerDetails} onClose={() => setCustomerDetails(null)} />
        )}
      </div>
    </div>
  );
};

export default ViewFollowUpsModal;
